// Mercapoli - Customer Home Page
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Clock, MapPin, Heart, ChevronRight, Loader2, Map } from 'lucide-react';
import CustomerLayout from '../../layouts/CustomerLayout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { categorias as categoriasApi, restaurantes as restaurantesApi, favoritos as favoritosApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useCity } from '../../context/CityContext';
import { toast } from 'sonner';

const CustomerHome = () => {
  const { user } = useAuth();
  const { ciudad, formatPrice } = useCity();
  const [searchParams] = useSearchParams();
  const busqueda = searchParams.get('busqueda') || '';

  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [restaurantes, setRestaurantes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [userFavoritos, setUserFavoritos] = useState([]);

  useEffect(() => {
    loadData();
  }, [busqueda, selectedCategory, ciudad]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar categorías
      const catsData = await categoriasApi.getAll();
      setCategorias(catsData);

      // Cargar restaurantes - filtrar por ciudad si está seleccionada
      const params = {};
      if (busqueda) params.busqueda = busqueda;
      if (selectedCategory) params.categoria_id = selectedCategory;
      if (ciudad?.ciudad_id) params.ciudad_id = ciudad.ciudad_id;
      
      const restsData = await restaurantesApi.getAll(params);
      setRestaurantes(restsData);

      // Cargar favoritos del usuario
      try {
        const favsData = await favoritosApi.getAll();
        setUserFavoritos(favsData.map(r => r.restaurante_id));
      } catch (e) {
        // Ignorar si no hay favoritos
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar restaurantes');
    }
    setLoading(false);
  };

  const toggleFavorito = async (restauranteId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      if (userFavoritos.includes(restauranteId)) {
        await favoritosApi.remove(restauranteId);
        setUserFavoritos(prev => prev.filter(id => id !== restauranteId));
        toast.success('Eliminado de favoritos');
      } else {
        await favoritosApi.add(restauranteId);
        setUserFavoritos(prev => [...prev, restauranteId]);
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const promos = [
    {
      id: 1,
      title: "20% OFF en tu primer pedido",
      code: "NUEVO20",
      bg: "bg-gradient-to-r from-spicy-coral to-[#FF8E53]",
      image: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400"
    },
    {
      id: 2,
      title: "Envío gratis en pedidos +$200",
      code: "ENVIOGRATIS",
      bg: "bg-gradient-to-r from-zesty-lime to-emerald-400",
      image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400"
    }
  ];

  return (
    <CustomerLayout>
      <div className="container-app py-4">
        {/* Saludo */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold">
                ¡Hola, {user?.nombre?.split(' ')[0]}! 👋
              </h1>
              <p className="text-muted-foreground">¿Qué se te antoja hoy?</p>
            </div>
            <Link to="/app/explorar">
              <Button variant="outline" className="gap-2">
                <Map className="h-4 w-4" />
                Ver mapa
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Banners promocionales */}
        <ScrollArea className="w-full mb-8">
          <div className="flex gap-4 pb-4">
            {promos.map((promo, index) => (
              <motion.div
                key={promo.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`${promo.bg} rounded-2xl p-6 min-w-[300px] sm:min-w-[350px] text-white relative overflow-hidden flex-shrink-0`}
              >
                <div className="relative z-10">
                  <h3 className="font-heading text-xl font-bold mb-2">{promo.title}</h3>
                  <p className="text-white/80 text-sm mb-3">Código: {promo.code}</p>
                  <Button size="sm" className="bg-white text-black hover:bg-white/90 rounded-full">
                    Usar cupón
                  </Button>
                </div>
                <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-30">
                  <img src={promo.image} alt="" className="h-full w-full object-cover" />
                </div>
              </motion.div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Categorías */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">Categorías</h2>
            <button 
              onClick={() => setSelectedCategory(null)}
              className="text-sm text-spicy-coral hover:underline"
            >
              Ver todas
            </button>
          </div>
          
          <ScrollArea className="w-full">
            <div className="flex gap-3 pb-4">
              {categorias.map((cat, index) => (
                <motion.button
                  key={cat.categoria_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedCategory(
                    selectedCategory === cat.categoria_id ? null : cat.categoria_id
                  )}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all flex-shrink-0 ${
                    selectedCategory === cat.categoria_id
                      ? 'bg-spicy-coral text-white shadow-glow'
                      : 'bg-card hover:bg-muted'
                  }`}
                  data-testid={`category-${cat.categoria_id}`}
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden">
                    <img 
                      src={cat.imagen} 
                      alt={cat.nombre}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs font-medium whitespace-nowrap">{cat.nombre}</span>
                </motion.button>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </section>

        {/* Resultados de búsqueda */}
        {busqueda && (
          <div className="mb-4">
            <p className="text-muted-foreground">
              Resultados para: <span className="font-medium text-foreground">"{busqueda}"</span>
            </p>
          </div>
        )}

        {/* Lista de restaurantes */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">
              {selectedCategory 
                ? `${categorias.find(c => c.categoria_id === selectedCategory)?.nombre || 'Restaurantes'}`
                : 'Restaurantes populares'}
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
            </div>
          ) : restaurantes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No se encontraron restaurantes</p>
              <Button onClick={() => { setSelectedCategory(null); }} variant="outline">
                Ver todos los restaurantes
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {restaurantes.map((restaurante, index) => (
                <motion.div
                  key={restaurante.restaurante_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link 
                    to={`/app/restaurante/${restaurante.restaurante_id}`}
                    className="group block"
                    data-testid={`restaurant-${restaurante.restaurante_id}`}
                  >
                    <div className="bg-card rounded-2xl overflow-hidden border border-border/50 card-hover">
                      {/* Imagen */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={restaurante.imagen}
                          alt={restaurante.nombre}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        
                        {/* Favorito button */}
                        <button
                          onClick={(e) => toggleFavorito(restaurante.restaurante_id, e)}
                          className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors"
                          data-testid={`fav-${restaurante.restaurante_id}`}
                        >
                          <Heart 
                            className={`h-5 w-5 ${
                              userFavoritos.includes(restaurante.restaurante_id)
                                ? 'fill-spicy-coral text-spicy-coral'
                                : 'text-gray-600'
                            }`}
                          />
                        </button>

                        {/* Status badge */}
                        {restaurante.abierto ? (
                          <Badge className="absolute top-3 left-3 bg-zesty-lime text-black">
                            Abierto
                          </Badge>
                        ) : (
                          <Badge className="absolute top-3 left-3 bg-gray-500">
                            Cerrado
                          </Badge>
                        )}

                        {/* Info overlay */}
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="font-heading text-lg font-bold text-white mb-1">
                            {restaurante.nombre}
                          </h3>
                          <p className="text-white/80 text-sm line-clamp-1">
                            {restaurante.descripcion}
                          </p>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-4">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="font-medium">{restaurante.calificacion}</span>
                              <span className="text-muted-foreground">
                                ({restaurante.total_resenas})
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span>{restaurante.tiempo_preparacion_min}-{restaurante.tiempo_preparacion_min + 15} min</span>
                            </div>
                          </div>
                          <span className="text-spicy-coral font-medium">
                            ${restaurante.costo_envio.toFixed(0)} envío
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>
    </CustomerLayout>
  );
};

export default CustomerHome;
